import { defaultPreferences, mergePreferences } from './registry';
import { validatePreferenceEnvelope } from './AccountSoundPreferences';

// The server document is authoritative. Drafts are memory-only, field-level
// edits so conflict recovery cannot silently overwrite another client's changes.
export class PreferenceStore {
  constructor({ service, current, notify, silence, scopeChanged, forbidden }) {
    Object.assign(this, { service, current, notify, silence, scopeChanged, forbidden });
    this.saved = null;
    this.patch = {};
    this.epoch = null;
    this.revision = null;
    this.state = { preferences: defaultPreferences(), status: 'loading', error: '', saveError: '', dirty: false, saving: false };
    this.controllers = new Set();
    this.disposed = false;
    this.healthy = false;
  }
  active() { return !this.disposed && this.current(); }
  emit(patch = {}) {
    this.state = { ...this.state, ...patch };
    if (this.active()) this.notify(this.state);
  }
  async request(operation) {
    const controller = new AbortController();
    this.controllers.add(controller);
    try { return validatePreferenceEnvelope(await operation(controller.signal)); }
    finally { this.controllers.delete(controller); }
  }
  apply(response, { conflict = false, committed = false } = {}) {
    const changed = this.epoch !== response.scope_epoch;
    if (!changed && this.revision !== null && response.revision < this.revision) {
      throw new Error('Out-of-order sound settings response');
    }
    const updated = changed || this.revision !== response.revision;
    if (updated) this.silence();
    // A new scope cannot inherit unsaved private settings from the old scope.
    if (committed || (changed && this.epoch !== null)) this.patch = {};
    this.saved = response.preferences;
    this.epoch = response.scope_epoch;
    this.revision = response.revision;
    this.healthy = true;
    const dirty = Object.keys(this.patch).length > 0;
    this.emit({
      preferences: mergePreferences(this.saved, this.patch), status: 'ready', dirty,
      saveError: dirty ? this.state.saveError : '',
      error: (conflict || (updated && dirty)) ?
        'Settings changed elsewhere. Your edits are retained; review and save again.' : '',
    });
    if (changed) this.scopeChanged();
  }
  async load(options = {}) {
    if (!this.active() || this.state.saving) return false;
    if (this.loading) return this.loading;
    this.loading = (async () => {
      try {
        const response = await this.request((signal) => this.service.load(signal));
        if (!this.active()) return false;
        this.apply(response, options);
        return true;
      } catch (error) {
        if (this.active()) this.fail(error, 'Could not load sound settings');
        return false;
      } finally { this.loading = null; }
    })();
    return this.loading;
  }
  fail(error, prefix) {
    this.healthy = false;
    this.silence();
    if (error.status === 403) { this.forbidden(); return; }
    this.emit({ status: this.saved ? 'ready' : 'error', error: `${prefix}: ${error.message || 'request failed'}` });
  }
  update(patch) {
    if (!this.active() || !this.saved || this.state.saving) return;
    const events = { ...(this.patch.events || {}) };
    Object.entries(patch.events || {}).forEach(([id, fields]) => {
      events[id] = { ...events[id], ...fields };
    });
    this.patch = { ...this.patch, ...patch, ...(Object.keys(events).length ? { events } : {}) };
    this.silence();
    this.emit({ preferences: mergePreferences(this.saved, this.patch), dirty: true });
  }
  async save() {
    if (!this.active() || !this.saved || !this.healthy || this.state.saving ||
        this.loading || !this.state.dirty) return;
    this.silence();
    this.emit({ saving: true, error: '', saveError: '' });
    try {
      const response = await this.request((signal) => this.service.save({
        revision: this.revision, preferences: this.state.preferences,
      }, signal));
      if (!this.active()) return;
      if (response.scope_epoch !== this.epoch) {
        this.apply(response);
        return;
      }
      if (response.revision <= this.revision) throw new Error('Unconfirmed sound settings revision');
      this.apply(response, { committed: true });
    } catch (error) {
      if (!this.active()) return;
      if (error.status === 409) {
        // Never retry the write automatically.
        this.emit({ saving: false, saveError: 'Settings changed elsewhere. Review and save again.' });
        await this.load({ conflict: true });
      } else {
        this.emit({ saveError: `Not saved: ${error.message || 'request failed'}` });
        this.fail(error, 'Not saved');
      }
    } finally {
      if (this.active()) this.emit({ saving: false });
    }
  }
  effective() {
    const draft = this.state.preferences;
    if (!this.healthy || !this.saved || this.state.saving) return { ...draft, masterEnabled: false };
    return { ...this.saved, masterEnabled: this.saved.masterEnabled && draft.masterEnabled,
      volume: Math.min(this.saved.volume, draft.volume),
      events: Object.fromEntries(Object.entries(this.saved.events).map(([id, entry]) => [
        id, { ...entry, enabled: entry.enabled === true && draft.events[id]?.enabled === true },
      ])) };
  }
  dispose() {
    this.disposed = true;
    this.controllers.forEach((controller) => controller.abort());
    this.controllers.clear();
    this.saved = null;
    this.patch = {};
    this.epoch = null;
    this.revision = null;
    this.healthy = false;
    this.state = { preferences: defaultPreferences(), status: 'error', error: 'Dispatcher access is unavailable.', saveError: '',
      dirty: false, saving: false };
  }
}
